import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { getLocalDateString } from '../utils/progressTracker';
import './StreakTracker.css';

const StreakTracker = ({ userId }) => {
  const [streaks, setStreaks] = useState({
    currentStreak: 0,
    longestStreak: 0,
    perfectStreak: 0,
    longestPerfectStreak: 0,
    totalReadings: 0,
    perfectDays: 0
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdateDate, setLastUpdateDate] = useState(null);

  useEffect(() => {
    if (userId) {
      calculateStreaks();
    }
  }, [userId]);

  const calculateStreaks = async () => {
    try {
      setLoading(true);

      // Fetch all scroll readings ordered by date
      const { data: readings, error } = await supabase
        .from('scroll_readings')
        .select('date, scroll_id, reading_number')
        .eq('user_id', userId)
        .order('date', { ascending: true });

      if (error) throw error;

      // Group readings by date
      const dailyData = {};
      readings.forEach(reading => {
        if (!dailyData[reading.date]) {
          dailyData[reading.date] = {
            readings: 0,
            scrolls: new Set()
          };
        }
        dailyData[reading.date].readings++;
        dailyData[reading.date].scrolls.add(reading.scroll_id);
      });

      // Sort dates
      const sortedDates = Object.keys(dailyData).sort();
      
      if (sortedDates.length === 0) {
        setStreaks({
          currentStreak: 0,
          longestStreak: 0,
          perfectStreak: 0,
          longestPerfectStreak: 0,
          totalReadings: 0,
          perfectDays: 0
        });
        setLoading(false);
        return;
      }

      // Calculate streaks
      let currentStreak = 0;
      let longestStreak = 0;
      let perfectStreak = 0;
      let longestPerfectStreak = 0;
      let totalReadings = readings.length;
      let perfectDays = 0;

      // Format a Date as a local YYYY-MM-DD string (matches how `date` is
      // stored on scroll_readings — never use toISOString here, it converts
      // to UTC first and silently shifts the date in positive-UTC-offset
      // timezones).
      const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = getLocalDateString();

      // Calculate current any-activity streak.
      // Always start from a fresh copy of `today` — never reuse/mutate a
      // shared Date object across the two streak loops below, or decrementing
      // one streak's checkDate corrupts the starting point for the other.
      let checkDate = new Date(today);
      let streakActive = true;

      // Start from today or yesterday, whichever has activity
      if (!dailyData[todayStr]) {
        checkDate.setDate(checkDate.getDate() - 1);
      }

      while (streakActive) {
        const dateStr = formatLocalDate(checkDate);
        if (dailyData[dateStr] && dailyData[dateStr].readings > 0) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          streakActive = false;
        }
      }

      // Calculate current perfect streak
      checkDate = new Date(today);
      streakActive = true;

      if (!dailyData[todayStr] || dailyData[todayStr].readings < 3) {
        checkDate.setDate(checkDate.getDate() - 1);
      }

      while (streakActive) {
        const dateStr = formatLocalDate(checkDate);
        if (dailyData[dateStr] && dailyData[dateStr].readings >= 3) {
          perfectStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          streakActive = false;
        }
      }

      // Calculate longest streaks by going through all dates
      let tempStreak = 0;
      let tempPerfectStreak = 0;
      let prevDate = null;

      sortedDates.forEach(dateStr => {
        const currentDate = new Date(dateStr);
        
        // Check for any-activity streak
        if (prevDate) {
          const dayDiff = Math.floor((currentDate - prevDate) / (1000 * 60 * 60 * 24));
          if (dayDiff === 1) {
            tempStreak++;
          } else {
            longestStreak = Math.max(longestStreak, tempStreak);
            tempStreak = 1;
          }
        } else {
          tempStreak = 1;
        }

        // Check for perfect streak
        if (dailyData[dateStr].readings >= 3) {
          perfectDays++;
          if (prevDate) {
            const dayDiff = Math.floor((currentDate - prevDate) / (1000 * 60 * 60 * 24));
            if (dayDiff === 1 && dailyData[prevDate.toISOString().split('T')[0]].readings >= 3) {
              tempPerfectStreak++;
            } else {
              longestPerfectStreak = Math.max(longestPerfectStreak, tempPerfectStreak);
              tempPerfectStreak = 1;
            }
          } else {
            tempPerfectStreak = 1;
          }
        } else {
          longestPerfectStreak = Math.max(longestPerfectStreak, tempPerfectStreak);
          tempPerfectStreak = 0;
        }

        prevDate = currentDate;
      });

      longestStreak = Math.max(longestStreak, tempStreak, currentStreak);
      longestPerfectStreak = Math.max(longestPerfectStreak, tempPerfectStreak, perfectStreak);

      setStreaks({
        currentStreak,
        longestStreak,
        perfectStreak,
        longestPerfectStreak,
        totalReadings,
        perfectDays
      });

      setLastUpdateDate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error calculating streaks:', error);
      setLoading(false);
    }
  };

  const getStreakMessage = () => {
    if (streaks.currentStreak === 0) {
      return "Start your journey today!";
    } else if (streaks.currentStreak === 1) {
      return "Great start! Keep it going tomorrow.";
    } else if (streaks.currentStreak < 7) {
      return `${streaks.currentStreak} days strong! You're building momentum.`;
    } else if (streaks.currentStreak < 30) {
      return `${streaks.currentStreak} days! This is becoming a habit.`;
    } else if (streaks.currentStreak < 100) {
      return `${streaks.currentStreak} days! You're unstoppable!`;
    } else {
      return `${streaks.currentStreak} days! You're a master of consistency!`;
    }
  };

  if (loading) {
    return (
      <div className="streak-tracker loading">
        <div className="loading-spinner"></div>
        <p>Calculating your progress...</p>
      </div>
    );
  }

  return (
    <div className="streak-tracker">
      <div className="streak-header">
        <h2>🔥 Your Streak</h2>
        <p className="streak-message">{getStreakMessage()}</p>
      </div>

      <div className="streak-main">
        <div className="streak-card primary">
          <div className="streak-icon">🔥</div>
          <div className="streak-content">
            <div className="streak-number">{streaks.currentStreak}</div>
            <div className="streak-label">Current Streak</div>
            <div className="streak-sublabel">days with any activity</div>
          </div>
        </div>

        <div className="streak-card secondary">
          <div className="streak-icon">⭐</div>
          <div className="streak-content">
            <div className="streak-number">{streaks.perfectStreak}</div>
            <div className="streak-label">Perfect Streak</div>
            <div className="streak-sublabel">days with 3 readings</div>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-value">{streaks.longestStreak}</div>
          <div className="stat-label">Longest Streak</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💎</div>
          <div className="stat-value">{streaks.longestPerfectStreak}</div>
          <div className="stat-label">Longest Perfect</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✓</div>
          <div className="stat-value">{streaks.perfectDays}</div>
          <div className="stat-label">Perfect Days</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📖</div>
          <div className="stat-value">{streaks.totalReadings}</div>
          <div className="stat-label">Total Readings</div>
        </div>
      </div>

      <div className="streak-footer">
        <button onClick={calculateStreaks} className="refresh-button">
          Refresh Stats
        </button>
      </div>
    </div>
  );
};

export default StreakTracker;
